import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { api } from "@/modules/api";
import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import { localStorageModule } from "@/modules/local-storage";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { compactWindowUrlForLogging } from "@/modules/window/compact-url-for-logging";
import {
  readIMessageConnectionClaimToken,
  removeIMessageConnectionClaimFromCurrentUrl,
} from "@/modules/mem-agent/imessage-connection-claim-url";
import type { SlackOAuthConnectionIntent } from "@/modules/local-storage/slack-oauth-state";
import { SlackOAuthClientKind } from "@/domains/integrations/slack-oauth-client-kind";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import type {
  MemAgentSlackConnectedModalViewEventParams,
  MemAgentSlackConnectSource,
  RemindersUnavailableDeliveryOption,
} from "@/domains/metrics/tracked-event";
import { buildMemAgentSlackConnectionContext } from "@/store/mem-agent/mem-agent-tracking-context";
import type { SubscriptionPlanKind } from "@/store/subscriptions/types";

interface AssistantApiResponse<ResponseData> {
  data?: ResponseData;
  error?: unknown;
}

interface AssistantApiClient {
  get<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
  post<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
  patch<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
  delete<ResponseData>(
    path: string,
    options?: Record<string, unknown>
  ): Promise<AssistantApiResponse<ResponseData>>;
}

export interface MemAgentAssistant {
  id: string;
  space_account_id: string;
  slug: string;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface ListAssistantsResponse {
  results: MemAgentAssistant[];
  total: number;
}

interface SlackConnectionSettingsResponse {
  is_connected: boolean;
  is_enabled: boolean;
  slack_workspace_id: string | null;
  slack_team_name: string | null;
  slack_app_url?: string | null;
  slack_workspace_url?: string | null;
  connected_at: string | null;
}

interface SlackConnectResponse {
  authorization_url: string;
  state: string;
}

interface WhatsAppConnectionSettingsResponse {
  is_connected: boolean;
  is_enabled: boolean;
  phone_number: string | null;
  connected_at: string | null;
}

export interface WhatsAppConnectResponse {
  activation_url: string;
  expires_at: string;
}

interface IMessageConnectionSettingsResponse {
  is_connected: boolean;
  is_enabled: boolean;
  phone_number: string | null;
  connected_at: string | null;
}

export interface IMessageConnectResponse {
  activation_url: string;
  activation_message: string;
  expires_at: string;
}

interface ClaimIMessageLeadConnectionArgs {
  claimToken: string;
  showToast?: boolean;
}

export interface MemAgentEmailAllowlistEntry {
  kind: "email" | "domain";
  value: string;
}

interface EmailSenderAllowlistResponse {
  assistant_id: string;
  email_sender_allowlist: MemAgentEmailAllowlistEntry[];
}

export interface UpdateMemAgentEmailSenderAllowlistArgs {
  emailSenderAllowlist: MemAgentEmailAllowlistEntry[];
}

interface PrepareSlackConnectionRedirectUrlArgs {
  showToast?: boolean;
  source?: MemAgentSlackConnectSource;
  isSetup?: boolean;
  connectionIntent?: SlackOAuthConnectionIntent;
  originalPlanKind?: SubscriptionPlanKind;
  originalEffectivePlanKind?: SubscriptionPlanKind;
}

interface ConnectSlackArgs {
  source?: MemAgentSlackConnectSource;
  isSetup?: boolean;
  connectionIntent?: SlackOAuthConnectionIntent;
  originalPlanKind?: SubscriptionPlanKind;
  originalEffectivePlanKind?: SubscriptionPlanKind;
}

interface OpenSlackConnectedModalArgs {
  trackingContext?: MemAgentSlackConnectedModalViewEventParams;
}

const assistantApi = api as unknown as AssistantApiClient;

const buildDisconnectedSlackSettings = (): SlackConnectionSettingsResponse => ({
  is_connected: false,
  is_enabled: false,
  slack_workspace_id: null,
  slack_team_name: null,
  slack_app_url: null,
  slack_workspace_url: null,
  connected_at: null,
});

const buildDisconnectedWhatsAppSettings = (): WhatsAppConnectionSettingsResponse => ({
  is_connected: false,
  is_enabled: false,
  phone_number: null,
  connected_at: null,
});

const buildDisconnectedIMessageSettings = (): IMessageConnectionSettingsResponse => ({
  is_connected: false,
  is_enabled: false,
  phone_number: null,
  connected_at: null,
});

const buildEmptyEmailSenderAllowlist = (): EmailSenderAllowlistResponse => ({
  assistant_id: "",
  email_sender_allowlist: [],
});

const getResponseErrorMessage = ({
  error,
  fallback,
}: {
  error: unknown;
  fallback: string;
}): string => {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.length > 0) {
      return detail;
    }
  }

  return fallback;
};

export class AppStoreMemAgentStore {
  private ready = false;
  private loading = false;
  private loadPromise: Promise<void> | null = null;
  private settingUp = false;
  private setupPromise: Promise<void> | null = null;
  private setupToastRequested = false;
  private updatingAssistant = false;
  private connectingSlack = false;
  private pendingSlackOAuthContext = localStorageModule.readSlackOAuthContext() !== null;
  private updatingSlack = false;
  private removingSlack = false;
  private connectingWhatsApp = false;
  private removingWhatsApp = false;
  private connectingIMessage = false;
  private removingIMessage = false;
  private updatingEmailSenderAllowlist = false;
  private slackConnectedModalOpen = false;
  private slackConnectedModalTrackingContext: MemAgentSlackConnectedModalViewEventParams | null =
    null;
  private remindersComingSoonModalOption: RemindersUnavailableDeliveryOption | null = null;
  private assistant: MemAgentAssistant | null = null;
  private slackSettings: SlackConnectionSettingsResponse = buildDisconnectedSlackSettings();
  private whatsappSettings: WhatsAppConnectionSettingsResponse =
    buildDisconnectedWhatsAppSettings();
  private imessageSettings: IMessageConnectionSettingsResponse =
    buildDisconnectedIMessageSettings();
  private emailSenderAllowlist: EmailSenderAllowlistResponse = buildEmptyEmailSenderAllowlist();

  constructor() {
    makeObservable<
      AppStoreMemAgentStore,
      | "ready"
      | "loading"
      | "loadPromise"
      | "settingUp"
      | "setupPromise"
      | "setupToastRequested"
      | "updatingAssistant"
      | "connectingSlack"
      | "pendingSlackOAuthContext"
      | "updatingSlack"
      | "removingSlack"
      | "connectingWhatsApp"
      | "removingWhatsApp"
      | "connectingIMessage"
      | "removingIMessage"
      | "updatingEmailSenderAllowlist"
      | "slackConnectedModalOpen"
      | "slackConnectedModalTrackingContext"
      | "remindersComingSoonModalOption"
      | "assistant"
      | "slackSettings"
      | "whatsappSettings"
      | "imessageSettings"
      | "emailSenderAllowlist"
      | "fetchSlackSettings"
      | "fetchWhatsAppSettings"
      | "fetchIMessageSettings"
      | "fetchEmailSenderAllowlist"
    >(this, {
      ready: observable,
      loading: observable,
      loadPromise: false,
      settingUp: observable,
      setupPromise: false,
      setupToastRequested: false,
      updatingAssistant: observable,
      connectingSlack: observable,
      pendingSlackOAuthContext: observable,
      updatingSlack: observable,
      removingSlack: observable,
      connectingWhatsApp: observable,
      removingWhatsApp: observable,
      connectingIMessage: observable,
      removingIMessage: observable,
      updatingEmailSenderAllowlist: observable,
      slackConnectedModalOpen: observable,
      slackConnectedModalTrackingContext: observable,
      remindersComingSoonModalOption: observable,
      assistant: observable,
      slackSettings: observable,
      whatsappSettings: observable,
      imessageSettings: observable,
      emailSenderAllowlist: observable,
      currentAssistant: computed,
      isReady: computed,
      isLoading: computed,
      isSettingUp: computed,
      isUpdatingAssistant: computed,
      isConnectingSlack: computed,
      hasPendingSlackOAuthContext: computed,
      isUpdatingSlack: computed,
      isRemovingSlack: computed,
      isConnectingWhatsApp: computed,
      isRemovingWhatsApp: computed,
      isConnectingIMessage: computed,
      isRemovingIMessage: computed,
      isUpdatingEmailSenderAllowlist: computed,
      isSlackConnectedModalOpen: computed,
      slackConnectedModalViewTrackingContext: computed,
      remindersComingSoonOption: computed,
      hasAssistant: computed,
      isAssistantEnabled: computed,
      assistantSlug: computed,
      isSlackConnected: computed,
      isSlackEnabled: computed,
      slackTeamName: computed,
      slackWorkspaceId: computed,
      slackAppUrl: computed,
      slackWorkspaceUrl: computed,
      isWhatsAppConnected: computed,
      isWhatsAppEnabled: computed,
      whatsAppPhoneNumber: computed,
      isIMessageConnected: computed,
      isIMessageEnabled: computed,
      imessagePhoneNumber: computed,
      emailSenderAllowlistEntries: computed,
      load: action,
      reload: action,
      setup: action,
      setAssistantEnabled: action,
      updateEmailSenderAllowlist: action,
      prepareSlackConnectionRedirectUrl: action,
      connectSlack: action,
      clearPendingSlackOAuthContext: action,
      setSlackEnabled: action,
      disconnectSlack: action,
      connectWhatsApp: action,
      disconnectWhatsApp: action,
      connectIMessage: action,
      claimIMessageLeadConnection: action,
      claimIMessageLeadConnectionFromUrl: action,
      disconnectIMessage: action,
      openSlackConnectedModal: action,
      closeSlackConnectedModal: action,
      openRemindersComingSoonModal: action,
      closeRemindersComingSoonModal: action,
      fetchSlackSettings: false,
      fetchWhatsAppSettings: false,
      fetchIMessageSettings: false,
      fetchEmailSenderAllowlist: false,
    });
  }

  get currentAssistant(): MemAgentAssistant | null {
    return this.assistant;
  }

  get isReady(): boolean {
    return this.ready;
  }

  get isLoading(): boolean {
    return this.loading;
  }

  get isSettingUp(): boolean {
    return this.settingUp;
  }

  get isUpdatingAssistant(): boolean {
    return this.updatingAssistant;
  }

  get isConnectingSlack(): boolean {
    return this.connectingSlack;
  }

  get hasPendingSlackOAuthContext(): boolean {
    return this.pendingSlackOAuthContext || localStorageModule.readSlackOAuthContext() !== null;
  }

  get isUpdatingSlack(): boolean {
    return this.updatingSlack;
  }

  get isRemovingSlack(): boolean {
    return this.removingSlack;
  }

  get isConnectingWhatsApp(): boolean {
    return this.connectingWhatsApp;
  }

  get isRemovingWhatsApp(): boolean {
    return this.removingWhatsApp;
  }

  get isConnectingIMessage(): boolean {
    return this.connectingIMessage;
  }

  get isRemovingIMessage(): boolean {
    return this.removingIMessage;
  }

  get isUpdatingEmailSenderAllowlist(): boolean {
    return this.updatingEmailSenderAllowlist;
  }

  get isSlackConnectedModalOpen(): boolean {
    return this.slackConnectedModalOpen;
  }

  get slackConnectedModalViewTrackingContext(): MemAgentSlackConnectedModalViewEventParams | null {
    return this.slackConnectedModalTrackingContext;
  }

  get remindersComingSoonOption(): RemindersUnavailableDeliveryOption | null {
    return this.remindersComingSoonModalOption;
  }

  get hasAssistant(): boolean {
    return this.assistant !== null;
  }

  get isAssistantEnabled(): boolean {
    return this.assistant?.is_enabled ?? false;
  }

  get assistantSlug(): string | null {
    return this.assistant?.slug ?? null;
  }

  get isSlackConnected(): boolean {
    return this.slackSettings.is_connected;
  }

  get isSlackEnabled(): boolean {
    return this.slackSettings.is_enabled;
  }

  get slackTeamName(): string | null {
    return this.slackSettings.slack_team_name;
  }

  get slackWorkspaceId(): string | null {
    return this.slackSettings.slack_workspace_id;
  }

  get slackAppUrl(): string | null {
    return this.slackSettings.slack_app_url ?? null;
  }

  get slackWorkspaceUrl(): string | null {
    return this.slackSettings.slack_workspace_url ?? null;
  }

  get isWhatsAppConnected(): boolean {
    return this.whatsappSettings.is_connected;
  }

  get isWhatsAppEnabled(): boolean {
    return this.whatsappSettings.is_enabled;
  }

  get whatsAppPhoneNumber(): string | null {
    return this.whatsappSettings.phone_number;
  }

  get isIMessageConnected(): boolean {
    return this.imessageSettings.is_connected;
  }

  get isIMessageEnabled(): boolean {
    return this.imessageSettings.is_enabled;
  }

  get imessagePhoneNumber(): string | null {
    return this.imessageSettings.phone_number;
  }

  get emailSenderAllowlistEntries(): MemAgentEmailAllowlistEntry[] {
    return this.emailSenderAllowlist.email_sender_allowlist;
  }

  /** Shares one assistant/settings hydration across concurrent callers. */
  load = (): Promise<void> => {
    this.loadPromise ??= (async () => {
      runInAction(() => {
        this.loading = true;
      });

      try {
        const response = await assistantApi.get<ListAssistantsResponse>("/v2/assistants", {});

        if (!response.data || response.error) {
          throw new Error(
            getResponseErrorMessage({
              error: response.error,
              fallback: "Failed to load Mem Agent settings",
            })
          );
        }

        const assistant = response.data.results[0] ?? null;
        const [slackSettings, whatsappSettings, imessageSettings, emailSenderAllowlist] = assistant
          ? await Promise.all([
              this.fetchSlackSettings({ assistantId: assistant.id }),
              this.fetchWhatsAppSettings({ assistantId: assistant.id }),
              this.fetchIMessageSettings({ assistantId: assistant.id }),
              this.fetchEmailSenderAllowlist({ assistantId: assistant.id }),
            ])
          : [
              buildDisconnectedSlackSettings(),
              buildDisconnectedWhatsAppSettings(),
              buildDisconnectedIMessageSettings(),
              buildEmptyEmailSenderAllowlist(),
            ];

        runInAction(() => {
          this.assistant = assistant;
          this.slackSettings = slackSettings;
          this.whatsappSettings = whatsappSettings;
          this.imessageSettings = imessageSettings;
          this.emailSenderAllowlist = emailSenderAllowlist;
        });
      } catch (unknownError) {
        const error = unknownError as Error;
        logger.error({
          message: "[AppStoreMemAgentStore] Failed to load Mem Agent settings",
          info: { error: objectModule.safeErrorAsJson(error) },
        });
      } finally {
        runInAction(() => {
          this.loading = false;
          this.ready = true;
        });
        this.loadPromise = null;
      }
    })();

    return this.loadPromise;
  };

  reload = async (): Promise<void> => {
    await this.load();
  };

  /**
   * Creates or retrieves the account's assistant.
   *
   * Concurrent setup callers share the same request so onboarding, widget
   * initialization, and a first send all wait for the assistant to exist.
   * If any joined caller requests feedback, the shared attempt toasts once.
   */
  setup = ({ showToast = true }: { showToast?: boolean } = {}): Promise<void> => {
    if (showToast) {
      this.setupToastRequested = true;
    }

    this.setupPromise ??= (async () => {
      runInAction(() => {
        this.settingUp = true;
      });

      try {
        const response = await assistantApi.post<MemAgentAssistant>("/v2/assistants", {
          body: {
            is_enabled: true,
          },
        });

        if (!response.data || response.error) {
          throw new Error(
            getResponseErrorMessage({
              error: response.error,
              fallback: "Failed to set up Reminders",
            })
          );
        }

        const assistant = response.data;
        runInAction(() => {
          this.assistant = assistant;
        });

        const [slackSettings, emailSenderAllowlist] = await Promise.all([
          this.fetchSlackSettings({
            assistantId: assistant.id,
          }),
          this.fetchEmailSenderAllowlist({
            assistantId: assistant.id,
          }),
        ]);

        runInAction(() => {
          this.slackSettings = slackSettings;
          this.emailSenderAllowlist = emailSenderAllowlist;
        });

        if (this.setupToastRequested) {
          toastModule.triggerToast({
            content: "Reminders set up.",
          });
        }
      } catch (unknownError) {
        const error = unknownError as Error;
        logger.error({
          message: "[AppStoreMemAgentStore] Failed to set up Mem Agent",
          info: { error: objectModule.safeErrorAsJson(error) },
        });

        if (this.setupToastRequested) {
          toastModule.triggerToast({
            content: error.message,
          });
        }
      } finally {
        runInAction(() => {
          this.settingUp = false;
          this.ready = true;
        });
        this.setupPromise = null;
        this.setupToastRequested = false;
      }
    })();

    return this.setupPromise;
  };

  setAssistantEnabled = async ({ isEnabled }: { isEnabled: boolean }): Promise<void> => {
    if (!this.assistant || this.updatingAssistant) return;

    const previousAssistant = this.assistant;

    runInAction(() => {
      this.updatingAssistant = true;
      this.assistant = {
        ...previousAssistant,
        is_enabled: isEnabled,
      };
    });

    try {
      const response = await assistantApi.patch<MemAgentAssistant>(
        `/v2/assistants/${previousAssistant.id}`,
        {
          body: {
            is_enabled: isEnabled,
          },
        }
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to update Reminders",
          })
        );
      }

      const assistant = response.data;

      runInAction(() => {
        this.assistant = assistant;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to update Mem Agent",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        this.assistant = previousAssistant;
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.updatingAssistant = false;
      });
    }
  };

  updateEmailSenderAllowlist = async ({
    emailSenderAllowlist,
  }: UpdateMemAgentEmailSenderAllowlistArgs): Promise<boolean> => {
    if (!this.assistant || this.updatingEmailSenderAllowlist) return false;

    runInAction(() => {
      this.updatingEmailSenderAllowlist = true;
    });

    try {
      const response = await assistantApi.patch<EmailSenderAllowlistResponse>(
        `/v2/assistants/${this.assistant.id}/email-sender-allowlist`,
        {
          body: {
            email_sender_allowlist: emailSenderAllowlist,
          },
        }
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to update email sender allowlist",
          })
        );
      }

      const emailSenderAllowlistResponse = response.data;

      runInAction(() => {
        this.emailSenderAllowlist = emailSenderAllowlistResponse;
      });

      toastModule.triggerToast({
        content: "Email sender allowlist saved.",
      });

      return true;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to update email sender allowlist",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: error.message,
      });

      return false;
    } finally {
      runInAction(() => {
        this.updatingEmailSenderAllowlist = false;
      });
    }
  };

  prepareSlackConnectionRedirectUrl = async ({
    showToast = true,
    source = "settings",
    isSetup = false,
    connectionIntent = "connect",
    originalPlanKind,
    originalEffectivePlanKind,
  }: PrepareSlackConnectionRedirectUrlArgs = {}): Promise<string | null> => {
    if (
      !this.assistant ||
      this.connectingSlack ||
      this.hasPendingSlackOAuthContext ||
      this.removingSlack
    ) {
      return null;
    }

    runInAction(() => {
      this.connectingSlack = true;
    });

    try {
      const clientKind = clientEnvModule.isDesktop()
        ? SlackOAuthClientKind.Desktop
        : SlackOAuthClientKind.Web;
      const queryParams = new URLSearchParams({
        client_kind: clientKind,
        connection_intent: connectionIntent,
        return_url: windowModule.getSlackOAuthRedirectUrl(),
      });
      const connectPath = `/v2/assistants/${this.assistant.id}/connections/slack/connect`;
      const response = await assistantApi.post<SlackConnectResponse>(
        `${connectPath}?${queryParams.toString()}`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to start Slack connection",
          })
        );
      }

      localStorageModule.writeSlackOAuthContext({
        state: response.data.state,
        connection_intent: connectionIntent,
        ...buildMemAgentSlackConnectionContext({
          source,
          isSetup,
          originalPlanKind,
          originalEffectivePlanKind,
        }),
      });
      runInAction(() => {
        this.pendingSlackOAuthContext = true;
      });

      return response.data.authorization_url;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to start Slack connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      if (showToast) {
        toastModule.triggerToast({
          content: error.message,
        });
      }
    } finally {
      runInAction(() => {
        this.connectingSlack = false;
      });
    }

    return null;
  };

  connectSlack = async ({
    source = "settings",
    isSetup = false,
    connectionIntent = "connect",
    originalPlanKind,
    originalEffectivePlanKind,
  }: ConnectSlackArgs = {}): Promise<void> => {
    const authorizationUrl = await this.prepareSlackConnectionRedirectUrl({
      source,
      isSetup,
      connectionIntent,
      originalPlanKind,
      originalEffectivePlanKind,
    });

    if (!authorizationUrl) {
      return;
    }

    trackEvent(TrackedEvent.MemAgentSlackConnectRedirect, {
      ...buildMemAgentSlackConnectionContext({
        source,
        isSetup,
        originalPlanKind,
        originalEffectivePlanKind,
      }),
      redirect_method: "direct",
    });

    try {
      const slackAuthorizationUrl = new URL(authorizationUrl);

      await windowModule.navigateToExternal({
        url: slackAuthorizationUrl,
      });
    } catch (unknownError) {
      const error = unknownError as Error;

      logger.error({
        message: "[AppStoreMemAgentStore] Failed to open Slack authorization URL",
        info: {
          error: objectModule.safeErrorAsJson(error),
          url: compactWindowUrlForLogging(authorizationUrl),
        },
      });

      this.clearPendingSlackOAuthContext();
      toastModule.triggerToast({
        content: "Unable to open Slack authorization in your browser. Please try again.",
      });
    }
  };

  clearPendingSlackOAuthContext = (): void => {
    localStorageModule.clearSlackOAuthState();
    this.pendingSlackOAuthContext = false;
  };

  openSlackConnectedModal = ({ trackingContext }: OpenSlackConnectedModalArgs = {}): void => {
    this.slackConnectedModalTrackingContext = trackingContext ?? null;
    this.slackConnectedModalOpen = true;
  };

  closeSlackConnectedModal = (): void => {
    this.slackConnectedModalOpen = false;
    this.slackConnectedModalTrackingContext = null;
  };

  /**
   * Opens the Reminders coming-soon modal for unavailable delivery channels.
   */
  openRemindersComingSoonModal = (option: RemindersUnavailableDeliveryOption): void => {
    this.remindersComingSoonModalOption = option;
  };

  /**
   * Closes the Reminders coming-soon modal.
   */
  closeRemindersComingSoonModal = (): void => {
    this.remindersComingSoonModalOption = null;
  };

  setSlackEnabled = async ({ isEnabled }: { isEnabled: boolean }): Promise<void> => {
    if (!this.assistant || !this.slackSettings.is_connected || this.updatingSlack) return;

    const previousSlackSettings = this.slackSettings;

    runInAction(() => {
      this.updatingSlack = true;
      this.slackSettings = {
        ...previousSlackSettings,
        is_enabled: isEnabled,
      };
    });

    try {
      const response = await assistantApi.patch<SlackConnectionSettingsResponse>(
        `/v2/assistants/${this.assistant.id}/connections/slack`,
        {
          body: {
            is_enabled: isEnabled,
          },
        }
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to update Slack connection",
          })
        );
      }

      const slackSettings = response.data;

      runInAction(() => {
        this.slackSettings = slackSettings;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to update Slack connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        this.slackSettings = previousSlackSettings;
      });

      toastModule.triggerToast({
        content: error.message,
      });
    } finally {
      runInAction(() => {
        this.updatingSlack = false;
      });
    }
  };

  disconnectSlack = async ({
    showToast = true,
  }: { showToast?: boolean } = {}): Promise<boolean> => {
    if (
      !this.assistant ||
      !this.slackSettings.is_connected ||
      this.connectingSlack ||
      this.hasPendingSlackOAuthContext ||
      this.removingSlack
    ) {
      return false;
    }

    const previousSlackSettings = this.slackSettings;

    runInAction(() => {
      this.removingSlack = true;
    });

    try {
      const response = await assistantApi.delete<SlackConnectionSettingsResponse>(
        `/v2/assistants/${this.assistant.id}/connections/slack`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to remove Slack account",
          })
        );
      }

      const slackSettings = response.data;

      runInAction(() => {
        this.slackSettings = slackSettings;
      });

      if (showToast) {
        toastModule.triggerToast({
          content: "Slack account removed.",
        });
      }

      trackEvent(TrackedEvent.MemAgentSlackDisconnect, {
        slack_workspace_id: previousSlackSettings.slack_workspace_id,
        slack_team_name: previousSlackSettings.slack_team_name,
      });

      return true;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to remove Slack account",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      runInAction(() => {
        this.slackSettings = previousSlackSettings;
      });

      if (showToast) {
        toastModule.triggerToast({
          content: error.message,
        });
      }

      return false;
    } finally {
      runInAction(() => {
        this.removingSlack = false;
      });
    }
  };

  connectWhatsApp = async (): Promise<WhatsAppConnectResponse | null> => {
    if (!this.assistant || this.connectingWhatsApp) return null;

    runInAction(() => {
      this.connectingWhatsApp = true;
    });

    try {
      const response = await assistantApi.post<WhatsAppConnectResponse>(
        `/v2/assistants/${this.assistant.id}/connections/whatsapp/connect`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to start WhatsApp connection",
          })
        );
      }

      return response.data;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to start WhatsApp connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      toastModule.triggerToast({
        content: error.message,
      });
      return null;
    } finally {
      runInAction(() => {
        this.connectingWhatsApp = false;
      });
    }
  };

  disconnectWhatsApp = async (): Promise<boolean> => {
    if (!this.assistant || !this.whatsappSettings.is_connected || this.removingWhatsApp) {
      return false;
    }

    const previousWhatsAppSettings = this.whatsappSettings;
    runInAction(() => {
      this.removingWhatsApp = true;
    });

    try {
      const response = await assistantApi.delete<WhatsAppConnectionSettingsResponse>(
        `/v2/assistants/${this.assistant.id}/connections/whatsapp`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to remove WhatsApp account",
          })
        );
      }

      const whatsappSettings = response.data;
      runInAction(() => {
        this.whatsappSettings = whatsappSettings;
      });
      toastModule.triggerToast({
        content: "WhatsApp account removed.",
      });
      return true;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to remove WhatsApp account",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      runInAction(() => {
        this.whatsappSettings = previousWhatsAppSettings;
      });
      toastModule.triggerToast({
        content: error.message,
      });
      return false;
    } finally {
      runInAction(() => {
        this.removingWhatsApp = false;
      });
    }
  };

  connectIMessage = async (): Promise<IMessageConnectResponse | null> => {
    if (!this.assistant || this.connectingIMessage) return null;

    runInAction(() => {
      this.connectingIMessage = true;
    });

    try {
      const response = await assistantApi.post<IMessageConnectResponse>(
        `/v2/assistants/${this.assistant.id}/connections/imessage/connect`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to start iMessage connection",
          })
        );
      }

      return response.data;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to start iMessage connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      toastModule.triggerToast({
        content: error.message,
      });
      return null;
    } finally {
      runInAction(() => {
        this.connectingIMessage = false;
      });
    }
  };

  claimIMessageLeadConnectionFromUrl = async (): Promise<boolean> => {
    const claimToken = readIMessageConnectionClaimToken({
      searchParams: windowModule.currentUrlSearchParams(),
    });
    if (claimToken === null) {
      return false;
    }

    removeIMessageConnectionClaimFromCurrentUrl();
    return this.claimIMessageLeadConnection({ claimToken });
  };

  claimIMessageLeadConnection = async ({
    claimToken,
    showToast = true,
  }: ClaimIMessageLeadConnectionArgs): Promise<boolean> => {
    if (this.connectingIMessage) {
      return false;
    }

    runInAction(() => {
      this.connectingIMessage = true;
    });
    trackEvent(TrackedEvent.MemAgentIMessageLeadClaimStart, {
      source: "login_url",
    });

    try {
      const response = await assistantApi.post<IMessageConnectionSettingsResponse>(
        "/v2/assistant-connection-leads/imessage/claim",
        {
          body: {
            claim_token: claimToken,
          },
        }
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "This iMessage setup link expired. Start again from Mem Agent settings.",
          })
        );
      }

      const imessageSettings = response.data;
      runInAction(() => {
        this.imessageSettings = imessageSettings;
        this.ready = true;
      });
      await this.load();
      trackEvent(TrackedEvent.MemAgentIMessageLeadClaimComplete, {
        is_enabled: imessageSettings.is_enabled,
        source: "login_url",
      });

      if (showToast) {
        toastModule.triggerToast({
          content: imessageSettings.is_enabled
            ? "iMessage connected to Mem Agent."
            : "iMessage connected. Finish Mem Agent setup in settings to enable it.",
        });
      }

      return true;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to claim iMessage lead connection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      if (showToast) {
        toastModule.triggerToast({
          content: error.message,
        });
      }
      trackEvent(TrackedEvent.MemAgentIMessageLeadClaimFailed, {
        failure_reason: "claim_request_failed",
        source: "login_url",
      });

      return false;
    } finally {
      runInAction(() => {
        this.connectingIMessage = false;
      });
    }
  };

  disconnectIMessage = async (): Promise<boolean> => {
    if (!this.assistant || !this.imessageSettings.is_connected || this.removingIMessage) {
      return false;
    }

    const previousIMessageSettings = this.imessageSettings;
    runInAction(() => {
      this.removingIMessage = true;
    });

    try {
      const response = await assistantApi.delete<IMessageConnectionSettingsResponse>(
        `/v2/assistants/${this.assistant.id}/connections/imessage`,
        {}
      );

      if (!response.data || response.error) {
        throw new Error(
          getResponseErrorMessage({
            error: response.error,
            fallback: "Failed to remove iMessage account",
          })
        );
      }

      const imessageSettings = response.data;
      runInAction(() => {
        this.imessageSettings = imessageSettings;
      });
      toastModule.triggerToast({
        content: "iMessage account removed.",
      });
      return true;
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreMemAgentStore] Failed to remove iMessage account",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
      runInAction(() => {
        this.imessageSettings = previousIMessageSettings;
      });
      toastModule.triggerToast({
        content: error.message,
      });
      return false;
    } finally {
      runInAction(() => {
        this.removingIMessage = false;
      });
    }
  };

  private fetchSlackSettings = async ({
    assistantId,
  }: {
    assistantId: string;
  }): Promise<SlackConnectionSettingsResponse> => {
    const response = await assistantApi.get<SlackConnectionSettingsResponse>(
      `/v2/assistants/${assistantId}/connections/slack`,
      {}
    );

    if (!response.data || response.error) {
      throw new Error(
        getResponseErrorMessage({
          error: response.error,
          fallback: "Failed to load Slack connection",
        })
      );
    }

    return response.data;
  };

  private fetchWhatsAppSettings = async ({
    assistantId,
  }: {
    assistantId: string;
  }): Promise<WhatsAppConnectionSettingsResponse> => {
    const response = await assistantApi.get<WhatsAppConnectionSettingsResponse>(
      `/v2/assistants/${assistantId}/connections/whatsapp`,
      {}
    );

    if (!response.data || response.error) {
      throw new Error(
        getResponseErrorMessage({
          error: response.error,
          fallback: "Failed to load WhatsApp connection",
        })
      );
    }

    return response.data;
  };

  private fetchIMessageSettings = async ({
    assistantId,
  }: {
    assistantId: string;
  }): Promise<IMessageConnectionSettingsResponse> => {
    const response = await assistantApi.get<IMessageConnectionSettingsResponse>(
      `/v2/assistants/${assistantId}/connections/imessage`,
      {}
    );

    if (!response.data || response.error) {
      throw new Error(
        getResponseErrorMessage({
          error: response.error,
          fallback: "Failed to load iMessage connection",
        })
      );
    }

    return response.data;
  };

  private fetchEmailSenderAllowlist = async ({
    assistantId,
  }: {
    assistantId: string;
  }): Promise<EmailSenderAllowlistResponse> => {
    const response = await assistantApi.get<EmailSenderAllowlistResponse>(
      `/v2/assistants/${assistantId}/email-sender-allowlist`,
      {}
    );

    if (!response.data || response.error) {
      throw new Error(
        getResponseErrorMessage({
          error: response.error,
          fallback: "Failed to load email sender allowlist",
        })
      );
    }

    return response.data;
  };
}
